export interface CollectionSection {
  heading: string;
  body: string[];
}

export interface Collection {
  id: string;
  title: string;
  category: string;
  summary: string;
  cover: string;
  drama: string;
  date: string;
  readTime: string;
  views: string;
  author: string;
  hot: boolean;
  tags: string[];
  keyPoints: string[];
  sections: CollectionSection[];
}

export const collections: Collection[] = [
  {
    id: "huaxu-night-cry",
    title: "《长夜未眠》哭戏笑场七次，最后一条导演也不忍心喊卡",
    category: "huaxu",
    summary:
      "同一场情绪戏连拍七条，前六条都因为笑场重来。第七条结束后现场安静了几秒，监视器后面没有立刻喊卡。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20behind%20the%20scenes%20film%20set%20corner%20with%20a%20wooden%20clapperboard%20and%20soft%20amber%20practical%20light%2C%20clean%20minimal%20background%2C%20subtle%20analog%20film%20grain%2C%20editorial%20still%20photography%20in%20warm%20neutral%20tones&width=800&height=600&seq=chigua-c-hx01&orientation=landscape",
    drama: "《长夜未眠》",
    date: "2026-08-28",
    readTime: "6 分钟",
    views: "3.2 万",
    author: "片场记录组",
    hot: true,
    tags: ["哭戏", "NG", "情绪戏"],
    keyPoints: [
      "同一条情绪戏连续拍摄七次，前六次均因笑场中断",
      "第七条结束后现场停顿数秒，监视器后没有立即喊卡",
      "最终采用并非用时最长的一条，而是呼吸节奏最稳的一条",
    ],
    sections: [
      {
        heading: "现场是怎么开始的",
        body: [
          "这一场戏安排在整天的最后一个时段拍摄，演员已经连续工作十多个小时。开场前导演只说了两句要求，大意是不要急着流泪，先把情绪压住，让观众自己发现。",
          "第一条走完，情绪推进得很快，现场所有人都以为可以过。结果在最后一个转身时，对手演员的一句即兴台词让全场出戏，连摄影师都抖了一下机器，第一条就这样作废。",
        ],
      },
      {
        heading: "七条之间发生了什么",
        body: [
          "接下来的几条各有各的问题：第二条是情绪过早爆发，第三条是走位偏了半个身位，第四到第六条则集中在笑场上。有趣的是，每次笑场之后演员都会主动要求重来，而不是让导演来决定。",
          "第六条结束后，现场安静了一会儿。灯光师把侧光调低了一档，理由是长时间拍摄后人眼对亮度的感受会变，画面需要重新平衡。这个细节在后面被证明很有用。",
        ],
      },
      {
        heading: "为什么留下的是第七条",
        body: [
          "第七条的情绪起点明显比前几条更低，演员前十几秒几乎没有动作，只是站在窗边。正是这段沉默让后面的爆发有了分量。导演在回看时反复播放了两个位置，最后选定了这一条。",
          "值得记下的是，被剪掉的六条并不是没有价值。在后续剪辑中，其中两条的局部反应被用在了背景的过场里，这也是为什么同一场戏在现场要拍这么多遍。",
        ],
      },
    ],
  },
  {
    id: "huaxu-south-letter",
    title: "《南风信箱》花絮：一封道具信写了三个版本",
    category: "huaxu",
    summary:
      "剧中反复出现的那封信，道具组前后写了三版。三个版本的差别不在内容，而在字迹的轻重与折痕的位置。",
    cover:
      "https://readdy.ai/api/search-image?query=Handwritten%20letter%20and%20a%20pen%20on%20a%20warm%20wooden%20desk%20under%20soft%20afternoon%20light%2C%20clean%20minimal%20background%2C%20subtle%20analog%20film%20grain%2C%20editorial%20still%20life%20photography%20with%20warm%20neutral%20tones&width=800&height=600&seq=chigua-c-hx02&orientation=landscape",
    drama: "《南风信箱》",
    date: "2026-08-21",
    readTime: "5 分钟",
    views: "2.4 万",
    author: "片场记录组",
    hot: false,
    tags: ["道具", "手写信", "细节"],
    keyPoints: [
      "同一封信准备了三个版本，区别在字迹力度与折痕位置",
      "第一版用于远景，第三版才用于手部特写",
      "道具组保留了每版的草稿，用于对比拍摄顺序",
    ],
    sections: [
      {
        heading: "一封信为什么要写三遍",
        body: [
          "这封信在剧中出现了四次，分别对应不同的情绪阶段。道具组的做法是不用同一张纸，而是按情节进度做旧、改字迹力度，让纸张的磨损程度跟着故事一起变化。",
          "第一版字迹工整、折痕规整，用于较早的时间线；第二版开始出现轻微的墨迹停顿；第三版则刻意让笔压变轻，纸角也有了一次反复展开的痕迹。",
        ],
      },
      {
        heading: "拍摄时怎么切换",
        body: [
          "远景和全景用第一版就够，因为观众看不清字迹。真正需要区分的是手部特写，这部分必须用第三版，否则和前面的情绪对不上。场记会在脚本上标注当前用的是第几版，避免穿帮。",
          "有一次因为拍摄顺序调整，特写先于中景拍完，道具组临时把第三版做了掩护处理，让它在中景里看起来不那么旧。这种临场调整在片场并不少见。",
        ],
      },
      {
        heading: "留下的细节",
        body: [
          "道具组最后把三个版本都留存了下来，按拍摄日期排列。对喜欢研究细节的观众来说，这三张纸其实是同一段关系的时间刻度，比台词更直接。",
        ],
      },
    ],
  },
  {
    id: "lutou-mist-port",
    title: "《雾港迷航》码头路透：雨戏连拍两个通宵",
    category: "lutou",
    summary:
      "外景定在真实的码头，连续两个通宵拍雨戏。现场靠两台造雨车维持雨量，风向一变整条就得重来。",
    cover:
      "https://readdy.ai/api/search-image?query=Rainy%20empty%20harbor%20dock%20at%20night%20with%20warm%20mooring%20lamps%20and%20wet%20reflective%20ground%2C%20moody%20minimal%20cinematic%20composition%2C%20subtle%20analog%20film%20grain%2C%20warm%20amber%20highlights&width=800&height=600&seq=chigua-c-lt01&orientation=landscape",
    drama: "《雾港迷航》",
    date: "2026-08-15",
    readTime: "7 分钟",
    views: "4.1 万",
    author: "外景跟进组",
    hot: true,
    tags: ["雨戏", "外景", "夜戏"],
    keyPoints: [
      "连续两个通宵拍摄，主要受风向与雨量稳定性影响",
      "现场使用两台造雨车，配合固定灯位制造层次",
      "湿地面反光成为画面主要光源之一，需要反复调整",
    ],
    sections: [
      {
        heading: "为什么选真实码头",
        body: [
          "美术上需要大量湿地面反光，棚内很难做出自然的质感。剧组最终选了老码头的一段空地，理由是地面材质吸水后反光均匀，配合岸边的暖色路灯能出层次。",
          "代价是现场条件不可控。潮气重、风大、地面湿滑，设备需要额外的防潮处理，走位也要避开积水最深的区域。",
        ],
      },
      {
        heading: "雨是造出来的，风是躲不开的",
        body: [
          "两台造雨车负责覆盖主要区域，雨量按镜头需求分档。问题是风向随时会变，一旦斜着吹进镜头，雨丝的走向就会和提前设计好的灯位冲突，只能暂停重来。",
          "第一个通宵里，同一段走廊戏拍了十几次，其中至少一半是因为风向。现场的解决方式是把造雨车位置固定下来，改由演员走位配合雨区，这样重拍的次数明显减少。",
        ],
      },
      {
        heading: "两个通宵之后",
        body: [
          "第二个通宵结束时天已经开始亮，剧组抢在自然光出现之前拍了最后几个收尾镜头。这类素材最终在成片中只出现几十秒，但现场消耗的时间和人力远超想象。",
        ],
      },
    ],
  },
  {
    id: "lutou-mountain-makeup",
    title: "《山海旧梦》定妆路透：从布衣到凤冠的三次调整",
    category: "lutou",
    summary:
      "同一角色的造型在路透里出现了三个版本，从素布衣到凤冠。变动不只是好看，而是跟剧情阶段对应。",
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20costume%20rack%20with%20layered%20traditional%20silks%20and%20jewelry%20in%20warm%20amber%20backstage%20light%2C%20clean%20minimal%20background%2C%20subtle%20analog%20film%20grain%2C%20editorial%20still%20life%20photography&width=800&height=600&seq=chigua-c-lt02&orientation=landscape",
    drama: "《山海旧梦》",
    date: "2026-08-09",
    readTime: "5 分钟",
    views: "2.9 万",
    author: "外景跟进组",
    hot: false,
    tags: ["造型", "定妆", "服装"],
    keyPoints: [
      "同一角色出现三套造型，分别对应剧情三个阶段",
      "布衣版的袖口与领口做了刻意磨损，凤冠版则加重了重量",
      "造型切换的节点在脚本里用颜色标记，避免混拍",
    ],
    sections: [
      {
        heading: "三套造型不是三件衣服",
        body: [
          "从路透能看到的最直观变化是衣料的层次。第一套几乎全是素色粗布，袖口有轻微起毛；第二套加入了暗纹与系带；第三套则换上织锦外披并配凤冠。",
          "造型团队的说法是，改动主要为了服务剧情阶段，而不是单纯追求华丽。素布衣阶段需要角色显得不起眼，凤冠阶段则需要有被压住的沉。",
        ],
      },
      {
        heading: "重量带来的表演变化",
        body: [
          "凤冠加上外披后整体重量增加不少，演员的走位幅度会自然收窄，坐姿也更端着。这恰好符合剧情后期的状态，所以造型组没有做减重处理。",
          "相对的，布衣版最好活动，很多跑动镜头都安排在素布衣阶段拍摄，既省力也更自然。",
        ],
      },
      {
        heading: "怎么避免混拍",
        body: [
          "因为拍摄顺序不等于剧情顺序，场记会在脚本上用不同颜色标出当前造型，并在每场开拍前确认一次。造型组同时准备了三套备用配件，防止磨损影响连续拍摄。",
        ],
      },
    ],
  },
  {
    id: "cepai-embroidery-light",
    title: "《绣楼春秋》侧拍手记：主光以外的第二现场",
    category: "cepai",
    summary:
      "当监视器盯着主光时，侧拍机位看到的是另一幅画面：反光板的位置、替身的走位、以及一条临时拉起的黑布。",
    cover:
      "https://readdy.ai/api/search-image?query=Soft%20warm%20light%20beam%20and%20a%20silver%20reflector%20panel%20on%20a%20quiet%20studio%20floor%2C%20deep%20shadows%2C%20minimal%20cinematic%20composition%2C%20subtle%20analog%20film%20grain%2C%20editorial%20photography&width=800&height=600&seq=chigua-c-cp01&orientation=landscape",
    drama: "《绣楼春秋》",
    date: "2026-08-02",
    readTime: "6 分钟",
    views: "1.8 万",
    author: "侧拍组",
    hot: false,
    tags: ["灯位", "机位", "现场调度"],
    keyPoints: [
      "主光之外，反光板的位置决定了面部转折的柔和程度",
      "临时拉起的黑布用于压住背景的杂散反光",
      "替身走位先行，用于确认光线覆盖后再换正式演员",
    ],
    sections: [
      {
        heading: "监视器看不到的地方",
        body: [
          "侧拍机位通常架在主机的斜后方，能看到整个布光结构。这一场里，主光是一盏带柔光箱的大灯，但真正决定画面质感的是左侧那块斜放的反光板。",
          "反光板挪动十几厘米，人物面部的暗部就会明显变化。灯光师在开拍前反复微调了几次，直到暗部保留住细节但不至于发灰。",
        ],
      },
      {
        heading: "黑布与替身",
        body: [
          "背景有一处反光会破坏景深，现场临时拉了一块黑布挡在灯架内侧。这块布不在画面里，却直接影响了背景的干净程度。",
          "正式演员到位之前，替身先走了一遍完整动线，用来确认光线在移动过程中的覆盖是否连续。这个步骤能省下大量正式拍摄的时间。",
        ],
      },
      {
        heading: "侧拍的价值",
        body: [
          "很多人看侧拍只关注演员状态，其实灯光与调度同样值得看。理解第二现场，才能明白成片里那些「刚刚好」的画面是怎么被计算出来的。",
        ],
      },
    ],
  },
  {
    id: "cepai-night-corridor",
    title: "《长夜未眠》侧拍角度：走廊尽头的那盏壁灯",
    category: "cepai",
    summary:
      "走廊戏的光源看似只有一盏壁灯，实际用了四组灯具维持层次。侧拍机位记录下了完整的补光布置过程。",
    cover:
      "https://readdy.ai/api/search-image?query=Dim%20hotel%20corridor%20at%20night%20with%20a%20single%20warm%20wall%20lamp%20glowing%2C%20long%20perspective%20and%20soft%20shadows%2C%20minimal%20cinematic%20composition%2C%20analog%20film%20grain%2C%20warm%20amber%20tones&width=800&height=600&seq=chigua-c-cp02&orientation=landscape",
    drama: "《长夜未眠》",
    date: "2026-07-26",
    readTime: "5 分钟",
    views: "2.1 万",
    author: "侧拍组",
    hot: false,
    tags: ["夜戏", "光线", "景深"],
    keyPoints: [
      "画面中只看到一盏壁灯，现场实际用了四组灯维持层次",
      "走廊尽头额外加了一块柔光布，避免人物出画后过暗",
      "地面反光板压低了亮度，让倒影不至于抢戏",
    ],
    sections: [
      {
        heading: "一盏灯的错觉",
        body: [
          "这一场走廊戏的画面很克制，几乎没有多余光源。但实际上，壁灯本身亮度不足，无法同时照亮人物和环境，灯光组在画面外的上方补了一盏定向灯。",
          "为了让补光不显得生硬，灯具前加了两层柔光纸，角度也压得很低，只在人物肩部形成一道柔和的轮廓。",
        ],
      },
      {
        heading: "走廊尽头为什么加布",
        body: [
          "走廊有纵深感，人物走到尽头时如果完全落入暗部，观众会失去参照。因此尽头位置加了一块半透光布，把亮度抬到一个刚好看得清轮廓的程度。",
          "同时地面上铺了一块低反射率的板，压住倒影。倒影太亮会把视线从人物身上拉走，这是夜戏里常见的处理。",
        ],
      },
      {
        heading: "看侧拍要注意什么",
        body: [
          "看这类侧拍时，可以重点关注「看起来只有一个光源」的画面，往往背后有最多的调整。这也是理解摄影风格最直接的方式。",
        ],
      },
    ],
  },
  {
    id: "caidan-mist-titles",
    title: "《雾港迷航》彩蛋收集：片头字幕里藏了六处呼应",
    category: "caidan",
    summary:
      "片头字幕不是单纯的名单。制作名单的排列顺序、字重变化和背景纹理，都和后文的关键情节存在对应关系。",
    cover:
      "https://readdy.ai/api/search-image?query=Vintage%20typewriter%20with%20paper%20and%20warm%20desk%20lamp%20light%2C%20clean%20minimal%20background%2C%20subtle%20analog%20film%20grain%2C%20editorial%20still%20life%20photography%20in%20warm%20neutral%20tones&width=800&height=600&seq=chigua-c-cd01&orientation=landscape",
    drama: "《雾港迷航》",
    date: "2026-07-19",
    readTime: "6 分钟",
    views: "3.6 万",
    author: "彩蛋整理组",
    hot: true,
    tags: ["片头", "字幕", "呼应"],
    keyPoints: [
      "片头字幕的背景纹理与后文出现的文件画面一致",
      "名单排序中有三个名字被刻意提前",
      "字幕字重变化对应剧情中的三段重要转折",
    ],
    sections: [
      {
        heading: "字幕也能埋线索",
        body: [
          "片头出现的背景并不是随机的噪点，而是把剧中一份关键文件做了模糊处理。暂停对照后可以发现，纸张边缘的折角位置和第四集出现的道具完全吻合。",
          "类似的呼应一共整理了六处，分别落在字幕背景、字体、排序和一行几乎看不清的小字上。",
        ],
      },
      {
        heading: "名单顺序为什么被改动",
        body: [
          "按常规，制作名单会按职务排列。这一版里，有三个名字被提前到了非常靠前的位置，而这三人恰好对应剧情中三个关键转折的推动者。",
          "这种处理并不显眼，但配合后续情节回看时，会觉得片头其实早已给出暗示。",
        ],
      },
      {
        heading: "怎么自己核对",
        body: [
          "建议先用 0.5 倍速至少看两遍片头，第一遍注意背景纹理，第二遍只看字体粗细的变化。把两次观察记录并排放在一起，对应关系会清晰很多。",
        ],
      },
    ],
  },
  {
    id: "caidan-south-clock",
    title: "《南风信箱》彩蛋：每集结尾的钟表都在走时",
    category: "caidan",
    summary:
      "剧中每集结尾都会出现同一只挂钟。整理后发现，钟面的时间并不是固定的，而是跟着本集剧情的发生时段移动。",
    cover:
      "https://readdy.ai/api/search-image?query=Antique%20wall%20clock%20with%20warm%20brass%20details%20on%20a%20minimal%20plaster%20wall%2C%20soft%20amber%20side%20light%2C%20clean%20composition%2C%20subtle%20analog%20film%20grain%2C%20editorial%20photography&width=800&height=600&seq=chigua-c-cd02&orientation=landscape",
    drama: "《南风信箱》",
    date: "2026-07-12",
    readTime: "5 分钟",
    views: "2.7 万",
    author: "彩蛋整理组",
    hot: false,
    tags: ["挂钟", "时间线", "道具"],
    keyPoints: [
      "每集结尾的挂钟时间与当集剧情时段一致",
      "钟摆摆动速度被刻意调慢，用于配合片尾音乐节奏",
      "第二次出现的钟面裂纹是后续剧情的关键伏笔",
    ],
    sections: [
      {
        heading: "一只钟的连续性",
        body: [
          "第一集结尾，挂钟指向傍晚；到了讲述凌晨事件的那一集，指针就停在深夜。把每集的时间记录下来排成一列，能得到一条完整的剧情时间轴。",
          "道具组确认，钟面是可以手动调节的，每次拍摄前都会按当集剧本里的时间设定好，并拍照留档。",
        ],
      },
      {
        heading: "钟摆与裂纹",
        body: [
          "钟摆的摆动速度比正常略慢，是为了贴合片尾音乐。至于第二次出现时的那道裂纹，剧本里并没有明确交代，是美术在做旧时加上的，后来被用作了后续剧情的伏笔。",
        ],
      },
      {
        heading: "整理成清单之后",
        body: [
          "把八集的时间点连起来，会发现整部剧的时间跨度恰好覆盖一个完整的季节。这个结论仅靠单集是看不出来的，也是分类整理的价值所在。",
        ],
      },
    ],
  },
  {
    id: "houchang-mountain-wait",
    title: "《山海旧梦》候场切片：灯光调试的四十分钟他们在做什么",
    category: "houchang",
    summary:
      "一场夜景开拍前的四十分钟通常被剪掉，但那段时间里演员在对词、造型在整理细节，导演在重排走位。",
    cover:
      "https://readdy.ai/api/search-image?query=Quiet%20backstage%20corner%20with%20a%20folding%20stool%20and%20a%20thermos%20under%20warm%20practical%20light%2C%20clean%20minimal%20background%2C%20subtle%20analog%20film%20grain%2C%20editorial%20still%20life%20photography&width=800&height=600&seq=chigua-c-hc01&orientation=landscape",
    drama: "《山海旧梦》",
    date: "2026-07-05",
    readTime: "6 分钟",
    views: "1.6 万",
    author: "候场记录组",
    hot: false,
    tags: ["候场", "对词", "走位"],
    keyPoints: [
      "灯光调试期间演员反复对词，调整情绪起点",
      "造型组利用这段时间修补服装磨损",
      "导演重新安排了两个走位节点，减少一次转场",
    ],
    sections: [
      {
        heading: "没有被拍进画面的四十分钟",
        body: [
          "夜景开拍前，现场需要完成灯位校准、地面处理与机位测试。这段时间演员通常已经到场，但不会立刻开拍，于是形成了片场特有的候场节奏。",
          "这一场里，两位演员利用这段时间把整段对话完整对了两遍，主要调整的是情绪起点。第一遍从平静开始，第二遍改成带着一点不耐烦，后面的表演方向随之不同。",
        ],
      },
      {
        heading: "候场不是空等",
        body: [
          "造型组趁这段时间修补了外披下摆的磨损，因为后续还有连续多天的拍摄，损耗会累积。道具组则把夜间要用的灯具重新排了位置。",
          "导演把两个走位节点做了合并，让演员少走一段路。这样不仅省时间，也避免在照明边缘出画。",
        ],
      },
      {
        heading: "为什么值得记录",
        body: [
          "候场片段看起来平淡，但它展示的是拍摄现场真正的节奏：大量时间花在准备上，真正开拍可能只有几分钟。理解这一点，再看花絮和路透会更有代入感。",
        ],
      },
    ],
  },
  {
    id: "houchang-embroidery-tea",
    title: "《绣楼春秋》候场片段：雪梨汤与临时改词",
    category: "houchang",
    summary:
      "冬戏拍摄期间，剧组在候场区备了雪梨汤。同一场戏里的一句台词被临时改写，理由和季节有关。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20enamel%20mug%20of%20tea%20on%20a%20wooden%20stool%20in%20a%20quiet%20backstage%20area%2C%20soft%20window%20light%2C%20clean%20minimal%20background%2C%20subtle%20analog%20film%20grain%2C%20editorial%20photography&width=800&height=600&seq=chigua-c-hc02&orientation=landscape",
    drama: "《绣楼春秋》",
    date: "2026-06-28",
    readTime: "5 分钟",
    views: "1.4 万",
    author: "候场记录组",
    hot: false,
    tags: ["候场", "改词", "剧组日常"],
    keyPoints: [
      "候场区常备热饮，用于应对连续低温拍摄",
      "一句台词的改动来自季节与道具状态的匹配",
      "改词后重新对了一遍节奏，确认时长不变",
    ],
    sections: [
      {
        heading: "候场区的日常",
        body: [
          "冬季外景的候场区通常离机位不远，摆着折叠椅、暖手宝和热饮。剧组备的是雪梨汤，理由是保温时间长、不需要频繁加热，也方便一口气喝完上场。",
          "这些安排看起来琐碎，但直接影响演员能否快速回到状态。冷了之后手指僵硬，很多细节动作就做不出来了。",
        ],
      },
      {
        heading: "一句台词为什么被改",
        body: [
          "原本的台词提到的是夏季的物件，但这一场是冬戏，道具桌上摆的都是应季的东西，画面和台词对不上。现场讨论后，改成了一句更贴合当下环境的说法。",
          "改完之后，导演让两位演员原地重新对了一遍，主要是确认改动后整段的时长没有变化，以免影响已经排好的走位。",
        ],
      },
      {
        heading: "候场的意义",
        body: [
          "类似的小改动在拍摄现场非常多，它们很少被记录，却决定了成片里那些看起来理所当然的细节。候场切片想留下的，正是这类过程。",
        ],
      },
    ],
  },
];